import React from "react";
import { cn } from "@/lib/utils";
import { useArtifactPanel } from "@/hooks/useArtifactPanel";
import { ArtifactPanelHeader } from "./panel/ArtifactPanelHeader";
import { ArtifactTabs } from "./panel/ArtifactTabs";
import { PanelResizeHandle } from "./panel/PanelResizeHandle";
import { ArtifactContentRenderer } from "./renderers/ArtifactContentRenderer";

interface ArtifactManagerProps {
  threadId: string;
}

const ArtifactManager: React.FC<ArtifactManagerProps> = ({ threadId }) => {
  const {
    isOpen,
    isFullscreen,
    panelWidth,
    threadItems,
    activeItem,
    activeArtifactId,
    closePanel,
    setActiveArtifact,
    handleRemoveItem,
    handleToggleFullscreen,
    handleResize,
    handleResizeStart,
    handleResizeEnd,
    handleDownloadChart,
    handleCopyChart,
    handleDownloadTable,
    handleCopyTable,
  } = useArtifactPanel({ threadId });

  const [key, setKey] = React.useState(0);

  // Don't render if panel is closed or no items
  if (!isOpen || threadItems.length === 0) {
    return null;
  }

  // Ensure active item exists
  const displayItem = activeItem || threadItems[0];
  if (!displayItem) {
    console.warn('Active item not found');
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col border-l border-border bg-background shadow-xl relative h-full",
        isFullscreen ? "fixed inset-0 z-50" : "flex-shrink-0",
      )}
      style={{
        width: isFullscreen ? '100vw' : `${panelWidth}%`,
        minWidth: isFullscreen ? '100vw' : '300px',
        maxWidth: isFullscreen ? '100vw' : '50vw',
      }}
    >
      {/* Resize Handle */}
      {!isFullscreen && (
        <PanelResizeHandle
          onResize={handleResize}
          onResizeStart={handleResizeStart}
          onResizeEnd={handleResizeEnd}
        />
      )}

      {/* Header */}
      <ArtifactPanelHeader
        activeItem={displayItem}
        isFullscreen={isFullscreen}
        onToggleFullscreen={() => {handleToggleFullscreen(); setKey(prev => prev + 1);}}
        onClose={closePanel}
        onDownloadChart={handleDownloadChart}
        onCopyChart={handleCopyChart}
        onDownloadTable={handleDownloadTable}
        onCopyTable={handleCopyTable}
      />

      {/* Tabs */}
      <ArtifactTabs
        items={threadItems}
        activeId={activeArtifactId || displayItem.id}
        onTabClick={setActiveArtifact}
        onRemoveItem={handleRemoveItem}
      />

      {/* Content - Fixed overflow */}
      <div className="flex-1 min-h-0 overflow-auto">
        <ArtifactContentRenderer item={displayItem} key={key}/>
      </div>
    </div>
  );
};

export default ArtifactManager;